/**
 * Generic mock data helpers
 *
 * Provides generic CRUD operations for any array-based mock data.
 * Features/entities just export an array - these helpers do the rest.
 */

import type {
  LaravelPaginationData,
  OffsetPaginatedResponse,
  OffsetPaginationQueryParams,
} from "@shared/contracts";
import { toOffsetMeta } from "@shared/contracts";

/**
 * The minimum shape the generic handlers need: an id to look up by.
 *
 * Laravel ids are integers, and its API resources expose no `created_at` /
 * `updated_at`, so there is nothing to stamp.
 */
interface MockEntityBase {
  id: number | string;
}

/**
 * Create generic CRUD handlers for an array of entities
 */
export function createMockHandlers<T extends MockEntityBase>(
  dataArray: T[],
  options?: {
    searchFields?: (keyof T)[];
  }
) {
  const search = (params?: Record<string, unknown>): T[] => {
    let filtered = [...dataArray];

    const term = params?.search as string | undefined;
    if (term && options?.searchFields) {
      const lowerSearch = term.toLowerCase();
      filtered = filtered.filter((item) =>
        options.searchFields!.some((field) => {
          const value = item[field];
          return (
            typeof value === "string" &&
            value.toLowerCase().includes(lowerSearch)
          );
        })
      );
    }

    return filtered;
  };

  const find = (id: string): T | undefined =>
    dataArray.find((item) => String(item.id) === id);

  return {
    get: (id: string): T => {
      const item = find(id);
      if (!item) {
        throw new Error(`Item with id ${id} not found`);
      }
      return item;
    },
    getAll: (params?: Record<string, unknown>): T[] => search(params),
    /**
     * Produces Laravel's `pagination_data` block and runs it through the
     * **real** `toOffsetMeta`, so mock mode exercises the same normalizer as
     * production. A bug in that mapping then cannot hide behind the mocks.
     */
    getPage: (
      query?: OffsetPaginationQueryParams
    ): OffsetPaginatedResponse<T> => {
      const filtered = search(query as Record<string, unknown> | undefined);
      const page = query?.page ?? 1;
      const limit = query?.limit ?? 10;
      const total = filtered.length;
      const totalPages = Math.max(1, Math.ceil(total / limit));
      const items = filtered.slice((page - 1) * limit, page * limit);

      const paginationData: LaravelPaginationData = {
        current_page: page,
        from: items.length ? (page - 1) * limit + 1 : null,
        to: items.length ? (page - 1) * limit + items.length : null,
        total,
        per_page: limit,
        total_pages: totalPages,
        is_first_page: page <= 1,
        is_last_page: page >= totalPages,
      };

      return { items, meta: toOffsetMeta(paginationData, items.length, query) };
    },
    create: (data: unknown): T => {
      const createData = data as Omit<T, "id">;
      const nextId =
        dataArray.reduce(
          (max, item) => Math.max(max, Number(item.id) || 0),
          0
        ) + 1;
      const newItem = { id: nextId, ...createData } as T;
      dataArray.push(newItem);
      return newItem;
    },
    update: (id: string, data: unknown): T => {
      const updateData = data as Partial<T>;
      const index = dataArray.findIndex((item) => String(item.id) === id);
      if (index === -1) {
        throw new Error(`Item with id ${id} not found`);
      }
      dataArray[index] = { ...dataArray[index], ...updateData };
      return dataArray[index];
    },
    delete: (id: string): void => {
      const index = dataArray.findIndex((item) => String(item.id) === id);
      if (index === -1) {
        throw new Error(`Item with id ${id} not found`);
      }
      dataArray.splice(index, 1);
    },
  };
}
